<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { formatBytes } from 'shared/helpers/FileHelper';
import { ATTACHMENT_TYPES } from 'dashboard/components-next/message/constants';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  showContact: { type: Boolean, default: false },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const dialogRef = ref(null);
const attachment = ref(null);

const fileType = computed(() => attachment.value?.file_type);
const isImage = computed(() => fileType.value === ATTACHMENT_TYPES.IMAGE);
const isVideo = computed(() =>
  [ATTACHMENT_TYPES.VIDEO, ATTACHMENT_TYPES.IG_REEL].includes(fileType.value)
);
const isAudio = computed(() => fileType.value === ATTACHMENT_TYPES.AUDIO);
const isPdf = computed(
  () => attachment.value?.extension?.toLowerCase() === 'pdf'
);

const fileName = computed(() => {
  const att = attachment.value;
  if (!att) return '';
  if (att.file_name) return att.file_name;
  if (att.data_url) {
    const name = att.data_url.split('/').pop();
    return name ? decodeURIComponent(name.split('?')[0]) : '';
  }
  return att.extension || '';
});

const fileIcon = computed(() => {
  if (isAudio.value) return 'i-lucide-music';
  if (isVideo.value) return 'i-lucide-video';
  if (isImage.value) return 'i-lucide-image';
  if (isPdf.value) return 'i-lucide-file-text';
  return 'i-lucide-file';
});

const directionLabel = computed(() => {
  if (!attachment.value?.direction) return '';
  return attachment.value.direction === 'sent'
    ? t('MEDIA_LIBRARY.VIEWER.SENT')
    : t('MEDIA_LIBRARY.VIEWER.RECEIVED');
});

const formattedDate = computed(() => {
  if (!attachment.value?.created_at) return '—';
  return messageTimestamp(attachment.value.created_at, 'MMM d, yyyy h:mm a');
});

const formattedSize = computed(() => {
  if (!attachment.value?.file_size) return '—';
  return formatBytes(attachment.value.file_size);
});

const sender = computed(() => attachment.value?.sender || null);

const open = item => {
  attachment.value = item;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onOpenExternal = () => {
  if (attachment.value?.data_url) {
    window.open(attachment.value.data_url, '_blank', 'noopener,noreferrer');
  }
};

const onViewConversation = () => {
  if (!attachment.value?.conversation_id) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: attachment.value.conversation_id,
    },
  });
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <div v-if="attachment" class="flex flex-col gap-4">
      <!-- Preview -->
      <div
        class="flex items-center justify-center w-full overflow-hidden rounded-lg bg-n-slate-3 min-h-48"
      >
        <img
          v-if="isImage"
          :src="attachment.data_url"
          :alt="fileName"
          class="object-contain w-full max-h-[60vh]"
        />
        <video
          v-else-if="isVideo"
          :src="attachment.data_url"
          controls
          class="w-full max-h-[60vh]"
        />
        <audio
          v-else-if="isAudio"
          :src="attachment.data_url"
          controls
          class="w-full px-4"
        />
        <iframe
          v-else-if="isPdf"
          :src="attachment.data_url"
          :title="fileName"
          class="w-full h-[60vh] bg-white"
        />
        <div v-else class="flex flex-col items-center justify-center gap-2 p-8">
          <Icon :icon="fileIcon" class="size-12 text-n-slate-10" />
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ fileName }}
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-wrap items-center gap-2">
        <a :href="attachment.data_url" :download="fileName">
          <NextButton
            sm
            blue
            icon="i-lucide-download"
            :label="t('MEDIA_LIBRARY.VIEWER.DOWNLOAD')"
          />
        </a>
        <NextButton
          sm
          slate
          faded
          icon="i-lucide-external-link"
          :label="t('MEDIA_LIBRARY.VIEWER.OPEN')"
          @click="onOpenExternal"
        />
        <NextButton
          v-if="attachment.conversation_id"
          sm
          slate
          faded
          icon="i-lucide-message-circle"
          :label="t('MEDIA_LIBRARY.VIEWER.VIEW_CONVERSATION')"
          @click="onViewConversation"
        />
      </div>

      <!-- Metadata -->
      <div class="flex flex-col gap-3 pt-3 border-t border-n-weak">
        <div class="flex items-center gap-2">
          <span class="w-24 text-xs font-medium text-n-slate-11 shrink-0">
            {{ t('MEDIA_LIBRARY.VIEWER.DIRECTION') }}
          </span>
          <span
            v-if="directionLabel"
            class="px-1.5 py-0.5 text-xxs font-medium rounded text-white"
            :class="
              attachment.direction === 'sent' ? 'bg-n-teal-9' : 'bg-n-slate-9'
            "
          >
            {{ directionLabel }}
          </span>
          <span v-else class="text-sm text-n-slate-11">{{
            t('MEDIA_LIBRARY.VIEWER.EMPTY')
          }}</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="w-24 text-xs font-medium text-n-slate-11 shrink-0">
            {{ t('MEDIA_LIBRARY.VIEWER.SENDER') }}
          </span>
          <div v-if="sender" class="flex items-center gap-2">
            <Avatar
              :src="sender.thumbnail"
              :name="sender.name || t('MEDIA_LIBRARY.VIEWER.UNKNOWN_SENDER')"
              :size="24"
              hide-offline-status
            />
            <span class="text-sm text-n-slate-12">
              {{ sender.name || t('MEDIA_LIBRARY.VIEWER.UNKNOWN_SENDER') }}
            </span>
          </div>
          <span v-else class="text-sm text-n-slate-11">{{
            t('MEDIA_LIBRARY.VIEWER.EMPTY')
          }}</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="w-24 text-xs font-medium text-n-slate-11 shrink-0">
            {{ t('MEDIA_LIBRARY.VIEWER.DATE') }}
          </span>
          <span class="text-sm text-n-slate-12">{{ formattedDate }}</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="w-24 text-xs font-medium text-n-slate-11 shrink-0">
            {{ t('MEDIA_LIBRARY.VIEWER.FILE') }}
          </span>
          <span class="text-sm truncate text-n-slate-12">{{ fileName }}</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="w-24 text-xs font-medium text-n-slate-11 shrink-0">
            {{ t('MEDIA_LIBRARY.VIEWER.SIZE') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ formattedSize }}
            <span v-if="attachment.extension" class="text-n-slate-10">
              {{ `· ${attachment.extension}` }}
            </span>
          </span>
        </div>

        <div
          v-if="showContact && attachment.contact"
          class="flex items-center gap-2"
        >
          <span class="w-24 text-xs font-medium text-n-slate-11 shrink-0">
            {{ t('MEDIA_LIBRARY.VIEWER.CONTACT') }}
          </span>
          <router-link
            :to="{
              name: 'contacts_edit',
              params: {
                accountId: route.params.accountId,
                contactId: attachment.contact.id,
              },
            }"
            class="text-sm text-n-blue-11 hover:underline"
          >
            {{ attachment.contact.name }}
          </router-link>
        </div>
      </div>
    </div>
  </Dialog>
</template>
